import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, Edit, Package, Calendar, Building2, User, RefreshCw, AlertTriangle, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  waiting_approval: 'bg-amber-100 text-amber-800',
  resubmitted: 'bg-purple-100 text-purple-800',
  sent: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

function POCard({ order, onView, onEdit, onReceive, onDelete }) {
  const statusClass = STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800';
  const canEdit = order.status === 'draft' || order.status === 'sent';
  const canEditRejected = order.status === 'rejected'; // Rejected POs can be edited and resubmitted
  const canReceive = ['sent', 'pending', 'approved', 'partially_received'].indexOf(order.status) >= 0;
  const isResubmitted = order.status === 'resubmitted';
  
  return (
    <Card data-testid={'po-card-' + order.id} className={`hover:shadow-md transition-shadow ${order.status === 'rejected' ? 'border-red-200 bg-red-50/30' : ''}`}>
      <CardContent className="p-4">
        {/* Rejection Banner for Rejected POs */}
        {order.status === 'rejected' && order.rejection_reason && (
          <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-md flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-red-800">Rejected: </span>
              <span className="text-red-700">{order.rejection_reason}</span>
              {order.rejected_by_name && (
                <span className="text-red-600 text-xs ml-2">by {order.rejected_by_name}</span>
              )}
            </div>
          </div>
        )}
        
        {/* Resubmitted Tag */}
        {isResubmitted && (
          <div className="mb-3 p-2 bg-purple-100 border border-purple-300 rounded-md flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-purple-800 font-medium">Resubmitted for Approval</span>
            {order.resubmitted_by_name && (
              <span className="text-purple-600 text-xs">by {order.resubmitted_by_name}</span>
            )}
          </div>
        )}
        
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold text-primary">{order.po_number}</h3>
              <Badge className={statusClass}>
                {order.status === 'waiting_approval' ? 'PENDING APPROVAL' : order.status.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Building2 size={14} />
                {order.supplier_name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {order.po_date}
              </span>
              {order.retailer_name && (
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {order.retailer_name}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {order.items.length} items
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-600">
              ₹{(order.total_amount || 0).toFixed(2)}
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={onView} data-testid={'view-po-' + order.id}>
                <Eye size={14} className="mr-1" /> View
              </Button>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={onEdit} data-testid={'edit-po-' + order.id}>
                  <Edit size={14} className="mr-1" /> Edit
                </Button>
              )}
              {canEditRejected && (
                <Button size="sm" onClick={onEdit} className="bg-orange-500 hover:bg-orange-600" data-testid={'edit-resubmit-' + order.id}>
                  <Edit size={14} className="mr-1" /> Edit & Resubmit
                </Button>
              )}
              {canReceive && (
                <Button size="sm" onClick={onReceive} data-testid={'receive-po-' + order.id}>
                  <Package size={14} className="mr-1" /> Receive
                </Button>
              )}
              {onDelete && (
                <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={'delete-po-' + order.id}>
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default POCard;
